/**
 * The document model behind the research-workflow generators.
 *
 * A consent form, a debriefing statement, an interview guide and a data management plan
 * are the same shape: a title, a few lines under it, then headed sections of
 * paragraphs, lists and fill-in lines. Each has to leave the page three ways — on screen,
 * as plain text for an email or a survey platform's text box, and as HTML that keeps its
 * headings and lists when pasted into Word or Google Docs. Building the document once as
 * data and rendering it three times is what keeps the three copies saying the same thing.
 *
 * Every word in a document comes from the calling widget's strings; this module only
 * decides layout.
 */

export type DocBlock =
  | { kind: "p"; text: string }
  /** A sub-heading inside a section — a template's numbered question. */
  | { kind: "h"; text: string }
  /** `start` continues an ordered list's numbering from an earlier section. */
  | { kind: "ul" | "ol"; items: string[]; start?: number }
  /** Fill-in lines — "Participant's signature: ____". One per item. */
  | { kind: "fields"; items: string[] };

export interface DocSection {
  heading: string;
  blocks: DocBlock[];
}

export interface DocModel {
  title: string;
  /** Lines under the title: the study, the researcher, a version date. */
  meta: string[];
  sections: DocSection[];
}

/** The blank a signature or a date is written on. */
export const FILL_LINE = "______________________________";

const LINE_BREAK = /\r?\n/;
const BULLET_PREFIX = /^\s*(?:[-*•–]|\d+[.)])\s+/;
const SPACE_RUN = /\s+/g;
const SENTENCE_END = /[.!?…]["'”’)\]]*$/;

/** Non-empty lines, trimmed — a paragraph per line the reader typed. */
export function linesOf(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Non-empty lines with any typed bullet or number stripped — a list per box. */
export function listItemsOf(text: string): string[] {
  return linesOf(text)
    .map((line) => line.replace(BULLET_PREFIX, "").trim())
    .filter((line) => line.length > 0);
}

/** Collapse runs of whitespace; the generators join fragments typed in many boxes. */
export function squish(text: string): string {
  return text.replace(SPACE_RUN, " ").trim();
}

/**
 * Split prose into sentences.
 *
 * `Intl.Segmenter` where the runtime has it (every current browser and Node), which knows
 * that "e.g. a survey" is one sentence; otherwise a plain scan that breaks after `.`,
 * `!` or `?` followed by a space.
 */
export function sentencesOf(text: string, locale = "en"): string[] {
  const clean = squish(text);
  if (clean === "") {
    return [];
  }
  if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
    try {
      const segmenter = new Intl.Segmenter(locale, { granularity: "sentence" });
      return Array.from(segmenter.segment(clean), ({ segment }) =>
        segment.trim()
      ).filter((segment) => segment.length > 0);
    } catch {
      // An unsupported locale tag: fall through to the plain scan.
    }
  }
  return scanSentences(clean);
}

function scanSentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const isEnd = char === "." || char === "!" || char === "?";
    if (isEnd && text[i + 1] === " ") {
      out.push(text.slice(start, i + 1).trim());
      start = i + 2;
    }
  }
  const rest = text.slice(start).trim();
  if (rest) {
    out.push(rest);
  }
  return out;
}

/** The first sentence of a box — what a summary quotes. Ends with punctuation. */
export function firstSentence(text: string, locale = "en"): string {
  const first = sentencesOf(text, locale)[0] ?? "";
  return ensurePeriod(first);
}

/** Add a closing full stop to a fragment that has no sentence-final punctuation. */
export function ensurePeriod(text: string): string {
  const clean = text.trim();
  if (clean === "" || SENTENCE_END.test(clean)) {
    return clean;
  }
  return `${clean}.`;
}

/* ------------------------------------------------------------------ rendering */

function blockToText(block: DocBlock): string {
  if (block.kind === "p" || block.kind === "h") {
    return block.text;
  }
  if (block.kind === "ol") {
    const start = block.start ?? 1;
    return block.items
      .map((item, index) => `${start + index}. ${item}`)
      .join("\n");
  }
  if (block.kind === "ul") {
    return block.items.map((item) => `• ${item}`).join("\n");
  }
  return block.items.join("\n\n");
}

/** Plain text: title, meta lines, then each heading in capitals over its blocks. */
export function docToText(doc: DocModel): string {
  const parts: string[] = [doc.title.toUpperCase()];
  if (doc.meta.length > 0) {
    parts.push(doc.meta.join("\n"));
  }
  for (const section of doc.sections) {
    const body = section.blocks.map(blockToText).join("\n\n");
    parts.push(
      section.heading ? `${section.heading.toUpperCase()}\n${body}` : body
    );
  }
  return parts.join("\n\n");
}

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
const HTML_SPECIAL = /[&<>"']/g;

export function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

function blockToHtml(block: DocBlock): string {
  if (block.kind === "p") {
    return `<p>${escapeHtml(block.text)}</p>`;
  }
  if (block.kind === "h") {
    return `<h3>${escapeHtml(block.text)}</h3>`;
  }
  if (block.kind === "fields") {
    return block.items.map((item) => `<p>${escapeHtml(item)}</p>`).join("");
  }
  const items = block.items
    .map((item) => `<li>${escapeHtml(item)}</li>`)
    .join("");
  const start =
    block.kind === "ol" && block.start && block.start > 1
      ? ` start="${block.start}"`
      : "";
  return `<${block.kind}${start}>${items}</${block.kind}>`;
}

/**
 * HTML for the clipboard: real headings and lists, so the paste into Word or Google
 * Docs arrives as a structured document rather than one long paragraph.
 */
export function docToHtml(doc: DocModel): string {
  const parts = [`<h1>${escapeHtml(doc.title)}</h1>`];
  if (doc.meta.length > 0) {
    parts.push(`<p>${doc.meta.map(escapeHtml).join("<br>")}</p>`);
  }
  for (const section of doc.sections) {
    if (section.heading) {
      parts.push(`<h2>${escapeHtml(section.heading)}</h2>`);
    }
    parts.push(...section.blocks.map(blockToHtml));
  }
  return parts.join("");
}

const MARKDOWN_SPECIAL = /([\\`*_[\]#|])/g;

/** Escape the characters Markdown would read as formatting. */
export function escapeMarkdown(text: string): string {
  return text.replace(MARKDOWN_SPECIAL, "\\$1");
}

function blockToMarkdown(block: DocBlock): string {
  if (block.kind === "p") {
    return escapeMarkdown(block.text);
  }
  if (block.kind === "h") {
    return `### ${escapeMarkdown(block.text)}`;
  }
  if (block.kind === "ol") {
    const start = block.start ?? 1;
    return block.items
      .map((item, index) => `${start + index}. ${escapeMarkdown(item)}`)
      .join("\n");
  }
  if (block.kind === "ul") {
    return block.items.map((item) => `- ${escapeMarkdown(item)}`).join("\n");
  }
  return block.items.map(escapeMarkdown).join("\n\n");
}

/** Markdown, for a README, a wiki page or a DMP pasted into a repository. */
export function docToMarkdown(doc: DocModel): string {
  const parts = [`# ${escapeMarkdown(doc.title)}`];
  if (doc.meta.length > 0) {
    parts.push(doc.meta.map(escapeMarkdown).join("  \n"));
  }
  for (const section of doc.sections) {
    if (section.heading) {
      parts.push(`## ${escapeMarkdown(section.heading)}`);
    }
    parts.push(...section.blocks.map(blockToMarkdown));
  }
  return `${parts.join("\n\n")}\n`;
}
